import React from "react";
import { FiCopy, FiEdit2, FiEye, FiPlus, FiTrash2, FiUpload, FiUserPlus, FiX } from "react-icons/fi";
import { LuCalendarDays } from "react-icons/lu";
import { MdOutlineBookmarkAdd } from "react-icons/md";
import DateTimePicker from "../../../components/DateTimePicker";
import { formatCurrency, formatTimestamp } from "../adminUtils";
import { handleEventImageError, resolveEventImageUrl } from "../../../utils/eventImages";
import { buildSelfAdjustingScheduleLabel } from "../../../utils/selfAdjustingScheduleLabel";
import "../../../styles/Info.scss";

const dateTimeLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isHistoricalEvent = (event) =>
  new Date(event?.endUtc).getTime() < Date.now();

const parsePreviewDate = (value, fallbackHour = 18) => {
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  const fallback = new Date();
  fallback.setHours(fallbackHour, 0, 0, 0);
  return fallback;
};

const formatPreviewEventDate = (date, language = "en") => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(language === "de" ? "de-AT" : "en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const formatPreviewDuration = (startDate, endDate) => {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) return "";
  const minutes = Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours > 0 && remainder > 0) return `${hours}h ${remainder}m`;
  if (hours > 0) return `${hours}h`;
  return `${remainder}m`;
};

const getMinutesOfDay = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? date.getHours() * 60 + date.getMinutes()
    : 0;

const isSameCalendarDay = (left, right) =>
  left instanceof Date &&
  right instanceof Date &&
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const SELF_ADJUSTING_WEEKDAYS = [
  { value: 1, key: "mon", fallback: "Mon" },
  { value: 2, key: "tue", fallback: "Tue" },
  { value: 3, key: "wed", fallback: "Wed" },
  { value: 4, key: "thu", fallback: "Thu" },
  { value: 5, key: "fri", fallback: "Fri" },
  { value: 6, key: "sat", fallback: "Sat" },
  { value: 0, key: "sun", fallback: "Sun" },
];

const timeToPreviewMinutes = (value, fallback) => {
  const [hoursRaw, minutesRaw] = String(value || "").split(":");
  const hours = Number.parseInt(hoursRaw, 10);
  const minutes = Number.parseInt(minutesRaw, 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallback;
  return Math.max(0, Math.min(1439, hours * 60 + minutes));
};

const buildPreviewTime = (date, minutes) => {
  const next = new Date(date);
  next.setHours(0, minutes, 0, 0);
  return next;
};

const chooseLocalizedText = (language, localized, fallback = "") => {
  const primary = language === "de" ? localized?.de : localized?.en;
  const secondary = language === "de" ? localized?.en : localized?.de;
  return primary?.trim() || secondary?.trim() || fallback;
};

const chooseLocalizedLines = (language, localized, fallback = []) => {
  const primary = language === "de" ? localized?.de : localized?.en;
  const secondary = language === "de" ? localized?.en : localized?.de;
  return primary?.length ? primary : secondary?.length ? secondary : fallback;
};

const getAvailableEquipmentIdsForLocation = (locations = [], locationId) => {
  const location = locations.find((item) => item.id === locationId);
  if (!location || !Array.isArray(location.inventory)) return new Set();

  return new Set(
    location.inventory
      .filter((item) => {
        const quantity = Number.parseInt(item.quantity, 10) || 0;
        const maintenance = Number.parseInt(item.maintenanceQuantity, 10) || 0;
        const lost = Number.parseInt(item.lostQuantity, 10) || 0;
        return quantity - maintenance - lost > 0;
      })
      .map((item) => item.equipmentTypeId)
      .filter(Boolean)
  );
};

const AdminEventsTab = ({
  t,
  language,
  isActive,
  locations,
  equipmentSnapshot = [],
  events,
  eventsLoading,
  eventsError,
  eventFormOpen,
  eventEditingId,
  eventForm,
  eventSaving,
  expandedEventId,
  eventActionId,
  eventTemplates,
  templatesLoading,
  selectedTemplateId,
  setSelectedTemplateId,
  imageUploading,
  attendeeForms,
  attendeeActionId,
  attendeeEditingId,
  attendeeEditForm,
  setEventFormOpen,
  setEventEditingId,
  setEventForm,
  setExpandedEventId,
  openEventForm,
  handleEventEdit,
  handleEventDuplicate,
  handleEventFormChange,
  handleApplyTemplate,
  handleSaveTemplateFromForm,
  handleUpdateSelectedTemplate,
  handleDeleteSelectedTemplate,
  handleEventImageUpload,
  handleEventSave,
  handleEventDelete,
  handleAttendeeFormChange,
  handleAddAttendee,
  handleRemoveAttendee,
  handleAttendeeEdit,
  handleAttendeeEditChange,
  handleAttendeeEditCancel,
  handleAttendeeUpdate,
}) => {
  const previewStartDate = parsePreviewDate(eventForm.startLocal, 18);
  const rawPreviewEndDate = parsePreviewDate(eventForm.endLocal, 22);
  const previewEndDate =
    rawPreviewEndDate > previewStartDate
      ? rawPreviewEndDate
      : new Date(previewStartDate.getTime() + 90 * 60000);
  const previewLocation =
    eventForm.eventLocationName?.trim() ||
    locations.find((location) => location.id === eventForm.locationId)?.name ||
    t("admin.events.chooseLocation", "Choose location");
  const previewPriceAmount = Number.parseFloat(eventForm.priceAmount);
  const previewPrice = formatCurrency(
    Number.isFinite(previewPriceAmount) ? previewPriceAmount : 0,
    "EUR"
  );
  const previewCapacity = Math.max(1, Number.parseInt(eventForm.capacity, 10) || 1);
  const previewIncludedEn = `${eventForm.includedTextEn || eventForm.includedText || ""}`
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  const previewIncludedDe = `${eventForm.includedTextDe || ""}`
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  const previewDescription = chooseLocalizedText(
    language,
    {
      en: eventForm.descriptionEn || eventForm.description,
      de: eventForm.descriptionDe,
    },
    t("admin.events.previewDescription", "Event description")
  );
  const previewIncluded = chooseLocalizedLines(
    language,
    {
      en: previewIncludedEn,
      de: previewIncludedDe,
    },
    []
  );
  const previewEvent = {
    title: eventForm.title || t("admin.events.previewTitle", "Event title"),
    description: previewDescription,
    detail: previewDescription,
    image: resolveEventImageUrl(eventForm.imageUrl),
    location: previewLocation,
    mapUrl: eventForm.mapUrl || "#",
    price: previewPrice,
    priceAmount: Number.isFinite(previewPriceAmount) ? previewPriceAmount : 0,
    duration: formatPreviewDuration(previewStartDate, previewEndDate),
    startDate: previewStartDate,
    endDate: previewEndDate,
    availablePlaces: previewCapacity,
    bookedCount: 0,
    remainingPlaces: previewCapacity,
    included: previewIncluded.length
      ? previewIncluded
      : [t("admin.events.previewIncluded", "Included items appear here")],
    isSelfAdjusting: Boolean(eventForm.isSelfAdjusting),
  };
  const previewCapacityPercent = previewEvent.availablePlaces
    ? Math.min(100, Math.round((previewEvent.bookedCount / previewEvent.availablePlaces) * 100))
    : 0;
  const previewSameDayWindow = isSameCalendarDay(previewEvent.startDate, previewEvent.endDate);
  const previewPickupStartMinutes = previewEvent.isSelfAdjusting
    ? timeToPreviewMinutes(eventForm.pickupStartTime, getMinutesOfDay(previewEvent.startDate))
    : previewSameDayWindow
      ? getMinutesOfDay(previewEvent.startDate)
      : 0;
  const previewPickupEndMinutes = previewEvent.isSelfAdjusting
    ? timeToPreviewMinutes(eventForm.pickupEndTime, getMinutesOfDay(previewEvent.endDate))
    : previewSameDayWindow
      ? getMinutesOfDay(previewEvent.endDate)
      : 1439;
  const previewReturnStartMinutes = previewEvent.isSelfAdjusting
    ? timeToPreviewMinutes(eventForm.returnStartTime, previewPickupStartMinutes)
    : previewPickupStartMinutes;
  const previewReturnEndMinutes = previewEvent.isSelfAdjusting
    ? timeToPreviewMinutes(eventForm.returnEndTime, previewPickupEndMinutes)
    : previewPickupEndMinutes;
  const previewScheduleLabel = buildSelfAdjustingScheduleLabel(
    {
      weekdays: eventForm.selfAdjustingWeekdays,
      pickupStartMinutes: previewPickupStartMinutes,
      pickupEndMinutes: previewPickupEndMinutes,
      returnStartMinutes: previewReturnStartMinutes,
      returnEndMinutes: previewReturnEndMinutes,
    },
    language
  );
  const previewPickupDate = buildPreviewTime(previewEvent.startDate, previewPickupStartMinutes);
  const previewReturnDate = buildPreviewTime(previewEvent.startDate, previewReturnEndMinutes);
  const availableEquipmentIds = getAvailableEquipmentIdsForLocation(
    locations,
    eventForm.locationId
  );
  const locationEquipmentSnapshot = (equipmentSnapshot || []).filter((equipment) =>
    availableEquipmentIds.has(equipment.id)
  );
  const editingEvent = eventEditingId
    ? events.find((event) => event.id === eventEditingId)
    : null;
  const isEditingBookedOccurrence = Boolean(editingEvent?.bookedCount);
  const toggleSelfAdjustingWeekday = (weekday) => {
    const current = Array.isArray(eventForm.selfAdjustingWeekdays)
      ? eventForm.selfAdjustingWeekdays.map(Number)
      : [];
    const next = current.includes(weekday)
      ? current.filter((item) => item !== weekday)
      : [...current, weekday];
    handleEventFormChange("selfAdjustingWeekdays", next);
  };
  const toggleEventEquipment = (equipmentId) => {
    const current = Array.isArray(eventForm.equipmentTypeIds)
      ? eventForm.equipmentTypeIds.filter(Boolean)
      : [];
    const next = current.includes(equipmentId)
      ? current.filter((item) => item !== equipmentId)
      : [...current, equipmentId];
    handleEventFormChange("equipmentTypeIds", next.length ? next : [eventForm.equipmentTypeId].filter(Boolean));
  };

  return (
  <div className={`admin-tab ${isActive ? "admin-tab--active" : ""}`.trim()}>
    <section className="admin-section admin-animate admin-animate--delay-5">
      <header>
        <div>
          <p className="admin-section__eyebrow">
            {t("admin.events.eyebrow", "Event management")}
          </p>
          <h2>{t("admin.events.heading", "Events")}</h2>
        </div>
        <button
          type="button"
          className="admin-button admin-button--primary"
          onClick={openEventForm}
        >
          <FiPlus aria-hidden="true" />
          {t("admin.events.add", "Add event")}
        </button>
      </header>

      {eventFormOpen && (
        <form className="admin-form admin-event-form" onSubmit={handleEventSave}>
          {isEditingBookedOccurrence && (
            <div className="admin-event-occurrence-notice" role="note">
              <strong>
                {t("admin.events.bookedOccurrence", "This occurrence already has attendees.")}
              </strong>{" "}
              {t(
                "admin.events.bookedOccurrenceHint",
                "Changing its date, time, or inventory location creates a new occurrence with an empty attendee list. The original occurrence and its bookings stay in history."
              )}
            </div>
          )}
          <div className="admin-event-tools">
            <label className="admin-event-tools__template">
              <span className="admin-form__label">
                {t("admin.events.template", "Template")}
              </span>
              <select
                className="admin-select"
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
                disabled={templatesLoading}
              >
                <option value="">
                  {templatesLoading
                    ? t("admin.events.templatesLoading", "Loading templates...")
                    : t("admin.events.chooseTemplate", "Choose template")}
                </option>
                {eventTemplates
                  .filter((template) => template.isActive)
                  .map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
              </select>
            </label>
            <div className="admin-inline-actions admin-inline-actions--compact">
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={handleApplyTemplate}
              >
                {t("admin.events.applyTemplate", "Apply")}
              </button>
              <button
                type="button"
                className="admin-button admin-button--outline"
                onClick={handleSaveTemplateFromForm}
              >
                {t("admin.events.saveTemplate", "Save as template")}
              </button>
              <button
                type="button"
                className="admin-button admin-button--outline"
                onClick={handleUpdateSelectedTemplate}
              >
                {t("admin.events.updateTemplate", "Update template")}
              </button>
              <button
                type="button"
                className="admin-button admin-button--danger"
                onClick={handleDeleteSelectedTemplate}
              >
                {t("admin.events.deleteTemplate", "Delete template")}
              </button>
            </div>
          </div>
          <div className="admin-form__grid admin-event-form__grid">
            <label className="admin-event-field admin-event-field--title">
              <span className="admin-form__label">{t("admin.events.title", "Title")}</span>
              <input
                className="admin-input"
                value={eventForm.title}
                onChange={(event) => handleEventFormChange("title", event.target.value)}
                required
              />
            </label>
            <label className="admin-event-field admin-event-field--location">
              <span className="admin-form__label">
                {t("admin.events.inventoryLocation", "Inventory location")}
              </span>
              <select
                className="admin-select"
                value={eventForm.locationId}
                onChange={(event) => handleEventFormChange("locationId", event.target.value)}
                required
              >
                <option value="">{t("admin.events.chooseLocation", "Choose location")}</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-event-field admin-event-field--location">
              <span className="admin-form__label">
                {t("admin.events.clientLocation", "Client-facing event location")}
              </span>
              <input
                className="admin-input"
                value={eventForm.eventLocationName}
                onChange={(event) =>
                  handleEventFormChange("eventLocationName", event.target.value)
                }
                placeholder={t(
                  "admin.events.clientLocationPlaceholder",
                  "Shown to customers, emails, and receipts"
                )}
              />
            </label>
            <label className="admin-event-field admin-event-field--date">
              <span className="admin-form__label">{t("admin.events.start", "Start")}</span>
              <input
                className="admin-input"
                type="datetime-local"
                value={eventForm.startLocal}
                onChange={(event) => handleEventFormChange("startLocal", event.target.value)}
                required
              />
            </label>
            <label className="admin-event-field admin-event-field--date">
              <span className="admin-form__label">{t("admin.events.end", "End")}</span>
              <input
                className="admin-input"
                type="datetime-local"
                value={eventForm.endLocal}
                onChange={(event) => handleEventFormChange("endLocal", event.target.value)}
                required
              />
            </label>
            {!eventForm.isSelfAdjusting && (
              <>
                <label className="admin-event-field admin-event-field--repeat">
                  <span className="admin-form__label">
                    {t("admin.events.repeat", "Repeat")}
                  </span>
                  <select
                    className="admin-select"
                    value={eventForm.repeatFrequency || "none"}
                    onChange={(event) =>
                      handleEventFormChange("repeatFrequency", event.target.value)
                    }
                  >
                    <option value="none">{t("admin.events.repeatNone", "Once")}</option>
                    <option value="weekly">{t("admin.events.repeatWeekly", "Weekly")}</option>
                    <option value="biweekly">
                      {t("admin.events.repeatBiweekly", "Every 2 weeks")}
                    </option>
                    <option value="monthly">{t("admin.events.repeatMonthly", "Monthly")}</option>
                  </select>
                </label>
                <label className="admin-event-field admin-event-field--small">
                  <span className="admin-form__label">
                    {t("admin.events.occurrences", "Occurrences")}
                  </span>
                  <input
                    className="admin-input"
                    type="number"
                    min="1"
                    max="52"
                    value={eventForm.repeatCount || "1"}
                    onChange={(event) => handleEventFormChange("repeatCount", event.target.value)}
                    disabled={(eventForm.repeatFrequency || "none") === "none"}
                  />
                </label>
              </>
            )}
            <label className="admin-event-field admin-event-field--small">
              <span className="admin-form__label">
                {t("admin.events.capacity", "Places")}
              </span>
              <input
                className="admin-input"
                type="number"
                min="1"
                value={eventForm.capacity}
                onChange={(event) => handleEventFormChange("capacity", event.target.value)}
                required
              />
            </label>
            <label className="admin-event-field admin-event-field--inventory">
              <span className="admin-form__label">
                {t("admin.events.equipment", "Inventory item")}
              </span>
              <select
                className="admin-select"
                value={eventForm.equipmentTypeId}
                onChange={(event) =>
                  handleEventFormChange("equipmentTypeId", event.target.value)
                }
                required
              >
                <option value="">
                  {t("admin.events.chooseEquipment", "Choose inventory item")}
                </option>
                {locationEquipmentSnapshot.map((equipment) => (
                  <option key={equipment.id} value={equipment.id}>
                    {equipment.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-event-field admin-event-field--small">
              <span className="admin-form__label">
                {t("admin.events.equipmentPerPerson", "Units per person")}
              </span>
              <input
                className="admin-input"
                type="number"
                min="1"
                value={eventForm.equipmentQuantityPerAttendee}
                onChange={(event) =>
                  handleEventFormChange(
                    "equipmentQuantityPerAttendee",
                    event.target.value
                  )
                }
                required
              />
            </label>
            <label className="admin-event-field admin-event-field--price">
              <span className="admin-form__label">{t("admin.events.price", "Price")}</span>
              <div className="admin-money-input">
                <input
                  className="admin-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={eventForm.priceAmount}
                  onChange={(event) => handleEventFormChange("priceAmount", event.target.value)}
                  required
                />
                <span>EUR</span>
              </div>
            </label>
            <label className="admin-event-field admin-event-field--image">
              <span className="admin-form__label">
                {t("admin.events.image", "Picture")}
              </span>
              <div className="admin-event-image-input">
                <input
                  className="admin-input"
                  value={eventForm.imageUrl}
                  onChange={(event) => handleEventFormChange("imageUrl", event.target.value)}
                  placeholder="/assets/pos_main.jpg"
                />
                <label className="admin-button admin-button--outline">
                  <FiUpload aria-hidden="true" />
                  {imageUploading
                    ? t("admin.events.uploading", "Uploading...")
                    : t("admin.events.upload", "Upload")}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      handleEventImageUpload(file);
                      event.target.value = "";
                    }}
                    disabled={imageUploading}
                  />
                </label>
              </div>
            </label>
          </div>
          {eventForm.isSelfAdjusting && (
            <fieldset className="admin-event-equipment-options">
              <legend>{t("admin.events.selectableEquipment", "Selectable equipment")}</legend>
              <p className="admin-item__note">
                {t(
                  "admin.events.selectableEquipmentHint",
                  "Customers can choose quantities from these inventory items in the event booking window."
                )}
              </p>
              <div className="admin-event-equipment-options__grid">
                {locationEquipmentSnapshot.map((equipment) => {
                  const selected = (eventForm.equipmentTypeIds || []).includes(equipment.id);
                  return (
                    <button
                      key={equipment.id}
                      type="button"
                      className={`admin-event-equipment-options__item ${
                        selected ? "admin-event-equipment-options__item--active" : ""
                      }`.trim()}
                      onClick={() => toggleEventEquipment(equipment.id)}
                      aria-pressed={selected}
                    >
                      {equipment.name}
                    </button>
                  );
                })}
                {!locationEquipmentSnapshot.length && (
                  <p className="admin-item__note">
                    {t(
                      "admin.events.noLocationEquipment",
                      "No inventory items are available at this location yet."
                    )}
                  </p>
                )}
              </div>
            </fieldset>
          )}
          <div className="admin-event-language-grid">
            <label className="admin-event-field admin-event-field--full">
              <span className="admin-form__label">
                {t("admin.events.descriptionEn", "Description (English)")}
              </span>
              <textarea
                className="admin-input admin-input--area"
                rows={3}
                value={eventForm.descriptionEn}
                onChange={(event) => {
                  handleEventFormChange("descriptionEn", event.target.value);
                  handleEventFormChange("description", event.target.value);
                }}
                required={!eventForm.descriptionDe?.trim()}
              />
            </label>
            <label className="admin-event-field admin-event-field--full">
              <span className="admin-form__label">
                {t("admin.events.descriptionDe", "Description (German)")}
              </span>
              <textarea
                className="admin-input admin-input--area"
                rows={3}
                value={eventForm.descriptionDe}
                onChange={(event) => handleEventFormChange("descriptionDe", event.target.value)}
                required={!eventForm.descriptionEn?.trim()}
              />
            </label>
            <label className="admin-event-field admin-event-field--full">
              <span className="admin-form__label">
                {t("admin.events.includedEn", "Included items (English)")}
              </span>
              <textarea
                className="admin-input admin-input--area"
                rows={3}
                value={eventForm.includedTextEn}
                onChange={(event) => {
                  handleEventFormChange("includedTextEn", event.target.value);
                  handleEventFormChange("includedText", event.target.value);
                }}
                placeholder={t("admin.events.includedHint", "One item per line")}
              />
            </label>
            <label className="admin-event-field admin-event-field--full">
              <span className="admin-form__label">
                {t("admin.events.includedDe", "Included items (German)")}
              </span>
              <textarea
                className="admin-input admin-input--area"
                rows={3}
                value={eventForm.includedTextDe}
                onChange={(event) => handleEventFormChange("includedTextDe", event.target.value)}
                placeholder={t("admin.events.includedHint", "One item per line")}
              />
            </label>
          </div>
          <label className="admin-event-field admin-event-field--full">
            <span className="admin-form__label">
              {t("admin.events.clientLocationAddress", "Client-facing event address")}
            </span>
            <input
              className="admin-input"
              value={eventForm.eventLocationAddress}
              onChange={(event) =>
                handleEventFormChange("eventLocationAddress", event.target.value)
              }
              placeholder={t(
                "admin.events.clientLocationAddressPlaceholder",
                "Optional address used in emails and receipts"
              )}
            />
          </label>
          <label className="admin-event-field admin-event-field--full">
            <span className="admin-form__label">
              {t("admin.events.map", "Event Google Maps URL")}
            </span>
            <input
              className="admin-input"
              value={eventForm.mapUrl}
              onChange={(event) => handleEventFormChange("mapUrl", event.target.value)}
            />
          </label>
          <label className="admin-check">
            <input
              type="checkbox"
              checked={Boolean(eventForm.isSelfAdjusting)}
              onChange={(event) => handleEventFormChange("isSelfAdjusting", event.target.checked)}
            />
            <span>
              {t(
                "admin.events.selfAdjusting",
                "Self-adjusting pickup and return"
              )}
            </span>
          </label>
          {eventForm.isSelfAdjusting && (
            <fieldset className="admin-event-schedule">
              <legend>{t("admin.events.selfSchedule", "Self-adjusting schedule")}</legend>
              <p className="admin-item__note">
                {t(
                  "admin.events.selfScheduleHint",
                  "Start and End define the available date range. Weekdays and the time range define when customers can pick up and return boards."
                )}
              </p>
              <div className="admin-event-schedule__weekdays">
                {SELF_ADJUSTING_WEEKDAYS.map((weekday) => {
                  const checked = (eventForm.selfAdjustingWeekdays || [])
                    .map(Number)
                    .includes(weekday.value);
                  return (
                    <button
                      key={weekday.value}
                      type="button"
                      className={`admin-event-schedule__day ${
                        checked ? "admin-event-schedule__day--active" : ""
                      }`.trim()}
                      onClick={() => toggleSelfAdjustingWeekday(weekday.value)}
                      aria-pressed={checked}
                    >
                      {t(`admin.events.weekdays.${weekday.key}`, weekday.fallback)}
                    </button>
                  );
                })}
              </div>
              <div className="admin-event-schedule__times">
                <label>
                  <span className="admin-form__label">
                    {t("admin.events.bookingWindowFrom", "From")}
                  </span>
                  <input
                    className="admin-input"
                    type="time"
                    value={eventForm.pickupStartTime || "18:00"}
                    onChange={(event) => {
                      handleEventFormChange("pickupStartTime", event.target.value);
                      handleEventFormChange("returnStartTime", event.target.value);
                    }}
                    required
                  />
                </label>
                <label>
                  <span className="admin-form__label">
                    {t("admin.events.bookingWindowUntil", "To")}
                  </span>
                  <input
                    className="admin-input"
                    type="time"
                    value={eventForm.pickupEndTime || "20:00"}
                    onChange={(event) => {
                      handleEventFormChange("pickupEndTime", event.target.value);
                      handleEventFormChange("returnEndTime", event.target.value);
                    }}
                    required
                  />
                </label>
              </div>
            </fieldset>
          )}
          <label className="admin-check">
            <input
              type="checkbox"
              checked={eventForm.isActive}
              onChange={(event) => handleEventFormChange("isActive", event.target.checked)}
            />
            <span>{t("admin.events.active", "Visible for booking")}</span>
          </label>
          <div className="admin-event-preview">
            <div className="admin-event-preview__label">
              <FiEye aria-hidden="true" />
              {t("admin.events.preview", "Preview")}
            </div>
            <div className="admin-event-preview__windows info-page">
              <section className="admin-event-preview__window">
                <h3>{t("admin.events.previewCard", "Info page card")}</h3>
                <div className="info-events__viewport admin-event-preview__viewport">
                  <div className="info-events__grid admin-event-preview__grid">
                    <article className="info-event-card admin-event-preview__card">
                      <div className="info-event-card__image">
                        <img
                          src={previewEvent.image}
                          alt={previewEvent.title}
                          onError={handleEventImageError}
                        />
                      </div>
                      <div className="info-event-card__body">
                        <div className="info-event-card__meta">
                          <span>
                            <LuCalendarDays aria-hidden="true" />
                            {previewEvent.isSelfAdjusting
                              ? previewScheduleLabel
                              : formatPreviewEventDate(previewEvent.startDate, language)}
                          </span>
                          <span>{previewEvent.price}</span>
                        </div>
                        <h3>{previewEvent.title}</h3>
                        <p>{previewEvent.description}</p>
                        <div className="info-event-card__footer">
                          <span>{previewEvent.location}</span>
                          <button type="button">{t("infoPage.events.book", "Book now")}</button>
                        </div>
                      </div>
                    </article>
                  </div>
                </div>
              </section>
              <section className="admin-event-preview__window admin-event-preview__window--modal">
                <h3>{t("admin.events.previewModal", "Opened booking window")}</h3>
                <div className="admin-event-preview__modal-stage">
                  <div className="profile-modal admin-event-preview__modal">
                    <div className="profile-modal__scrim" aria-hidden="true" />
                    <div className="profile-modal__content admin-event-preview__modal-content">
                      <div className="profile-modal__header">
                        <h3>{previewEvent.title}</h3>
                        <button
                          type="button"
                          className="profile-modal__close"
                          aria-label={t("profile.modal.close", "Close")}
                        >
                          <FiX aria-hidden="true" />
                        </button>
                      </div>
                      <div className="profile-modal__body">
                        <div className="info-event-modal">
                          <div className="info-event-modal__hero">
                            <div className="info-event-modal__visual">
                              <img
                                src={previewEvent.image}
                                alt={previewEvent.title}
                                onError={handleEventImageError}
                              />
                            </div>
                            <div className="info-event-modal__intro">
                              <p className="info-event-modal__eyebrow">
                                {t("infoPage.events.modal.eventDetails", "Event details")}
                              </p>
                              <p className="info-event-modal__description">{previewEvent.detail}</p>
                              <p className="info-event-modal__secondary">
                                {previewEvent.description}
                              </p>
                            </div>
                          </div>

                          <div className="info-event-modal__facts">
                            <div className="info-event-modal__fact">
                              <span>{t("infoPage.events.modal.time", "Time")}</span>
                              <strong>
                                {formatPreviewEventDate(previewEvent.startDate, language)}
                              </strong>
                              <small>
                                {formatPreviewEventDate(previewEvent.endDate, language)}
                              </small>
                            </div>
                            <a
                              className="info-event-modal__fact info-event-modal__fact--link"
                              href={previewEvent.mapUrl}
                              onClick={(event) => event.preventDefault()}
                            >
                              <span>{t("infoPage.events.modal.location", "Location")}</span>
                              <strong>{previewEvent.location}</strong>
                              <small>
                                {t("infoPage.events.modal.openMaps", "Open in Google Maps")}
                              </small>
                            </a>
                            <div className="info-event-modal__fact">
                              <span>{t("infoPage.events.modal.duration", "Duration")}</span>
                              <strong>{previewEvent.duration}</strong>
                              <small>{previewEvent.price}</small>
                            </div>
                          </div>

                          {previewEvent.isSelfAdjusting && (
                            <div className="info-event-modal__time-choice">
                              <div>
                                <h4>
                                  {t(
                                    "infoPage.events.modal.pickupReturn",
                                    "Choose pickup and return"
                                  )}
                                </h4>
                                <p>
                                  {t(
                                    "infoPage.events.modal.pickupReturnHint",
                                    "Pick any available time inside the event window."
                                  )}
                                </p>
                              </div>
                              <div className="info-event-modal__pickers info-event-modal__pickers--self-adjusting">
                                <div className="info-event-modal__picker-panel info-event-modal__picker-panel--date">
                                  <p className="info-event-modal__picker-label">
                                    {t("infoPage.events.modal.eventDate", "Event date")}
                                  </p>
                                  <DateTimePicker
                                    initialDate={previewPickupDate}
                                    onSelectionChange={() => {}}
                                    showDurationControls={false}
                                    showTimePicker={false}
                                    syncInitialTime={false}
                                    minMinutes={previewPickupStartMinutes}
                                    maxMinutes={previewPickupEndMinutes}
                                    selectableStartDate={previewEvent.startDate}
                                    selectableStartDateTime={previewPickupDate}
                                    selectableEndDate={previewEvent.endDate}
                                    timelineStartMinutes={previewPickupStartMinutes}
                                    timelineEndMinutes={previewPickupEndMinutes}
                                    stepMinutes={10}
                                  />
                                </div>
                                <div className="info-event-modal__time-stack">
                                  <div className="info-event-modal__picker-panel info-event-modal__picker-panel--pickup">
                                    <p className="info-event-modal__picker-label">
                                      {t("infoPage.events.modal.pickup", "Pickup")}
                                    </p>
                                    <DateTimePicker
                                      initialDate={previewPickupDate}
                                      onSelectionChange={() => {}}
                                      showDurationControls={false}
                                      minMinutes={previewPickupStartMinutes}
                                      maxMinutes={previewPickupEndMinutes}
                                      selectableStartDate={previewEvent.startDate}
                                      selectableStartDateTime={previewPickupDate}
                                      selectableEndDate={previewEvent.endDate}
                                      timelineStartMinutes={previewPickupStartMinutes}
                                      timelineEndMinutes={previewPickupEndMinutes}
                                      stepMinutes={10}
                                    />
                                  </div>
                                  <div className="info-event-modal__picker-panel info-event-modal__picker-panel--return">
                                    <p className="info-event-modal__picker-label">
                                      {t("infoPage.events.modal.return", "Return")}
                                    </p>
                                    <DateTimePicker
                                      initialDate={previewReturnDate}
                                      onSelectionChange={() => {}}
                                      showDurationControls={false}
                                      minMinutes={previewReturnStartMinutes}
                                      maxMinutes={previewReturnEndMinutes}
                                      selectableStartDate={previewEvent.startDate}
                                      selectableStartDateTime={previewReturnDate}
                                      selectableEndDate={previewEvent.endDate}
                                      timelineStartMinutes={previewReturnStartMinutes}
                                      timelineEndMinutes={previewReturnEndMinutes}
                                      stepMinutes={10}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="info-event-modal__capacity">
                            <div>
                              <span>{t("infoPage.events.modal.availability", "Availability")}</span>
                              <strong>
                                {t(
                                  "infoPage.events.modal.bookedCount",
                                  "{booked} booked of {total} places"
                                )
                                  .replace("{booked}", String(previewEvent.bookedCount))
                                  .replace("{total}", String(previewEvent.availablePlaces))}
                              </strong>
                            </div>
                            <span>
                              {previewEvent.remainingPlaces} {t("infoPage.events.modal.left", "left")}
                            </span>
                            <div className="info-event-modal__capacity-track" aria-hidden="true">
                              <span style={{ width: `${previewCapacityPercent}%` }} />
                            </div>
                          </div>

                          <div className="info-event-modal__included">
                            <h4>{t("infoPage.events.modal.included", "Included")}</h4>
                            <ul>
                              {previewEvent.included.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <div className="info-event-modal__actions">
                            <div className="info-event-modal__total">
                              <span>{t("infoPage.events.modal.total", "Total")}</span>
                              <strong>EUR {previewEvent.priceAmount.toFixed(2)}</strong>
                            </div>
                            <button
                              type="button"
                              className="info-event-btn info-event-btn--primary"
                            >
                              <MdOutlineBookmarkAdd aria-hidden="true" />
                              {t("infoPage.events.modal.request", "Request booking")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
            {!eventForm.isActive && (
              <p className="admin-item__note">
                {t(
                  "admin.events.previewHidden",
                  "This preview is private. The event appears on the Info page only after Visible for booking is checked and saved."
                )}
              </p>
            )}
          </div>
          <div className="admin-inline-actions">
            <button
              type="submit"
              className="admin-button admin-button--primary"
              disabled={eventSaving}
            >
              {eventEditingId
                ? t("admin.events.save", "Save event")
                : t("admin.events.create", "Create event")}
            </button>
            <button
              type="button"
              className="admin-button admin-button--ghost"
              onClick={() => {
                setEventFormOpen(false);
                setEventEditingId(null);
                setEventForm((prev) => ({ ...prev }));
              }}
            >
              {t("admin.actions.cancel", "Cancel")}
            </button>
          </div>
        </form>
      )}

      <div className="admin-bookings admin-events">
        {eventsLoading ? (
          <p className="admin-empty">{t("admin.events.loading", "Loading events...")}</p>
        ) : eventsError ? (
          <p className="admin-empty">{eventsError}</p>
        ) : events.length === 0 ? (
          <p className="admin-empty">
            {t("admin.events.empty", "No events have been created yet.")}
          </p>
        ) : (
          events.map((event, index) => (
            <React.Fragment key={event.id}>
              {(index === 0 ||
                (isHistoricalEvent(event) && !isHistoricalEvent(events[index - 1]))) && (
                <div className="admin-events__group-heading">
                  <div>
                    <p className="admin-section__eyebrow">
                      {isHistoricalEvent(event)
                        ? t("admin.events.history", "Event history")
                        : t("admin.events.upcoming", "Upcoming occurrences")}
                    </p>
                    <p className="admin-item__note">
                      {isHistoricalEvent(event)
                        ? t(
                            "admin.events.historyHint",
                            "Past occurrences keep their own configuration and attendee records."
                          )
                        : t(
                            "admin.events.upcomingHint",
                            "Each occurrence has an independent date, capacity, and attendee list."
                          )}
                    </p>
                  </div>
                </div>
              )}
            <article className="admin-booking admin-event">
              <div className="admin-booking__main">
                <div>
                  <h3>{event.title}</h3>
                  <p className="admin-booking__sub">{event.locationName}</p>
                  <p className="admin-booking__meta">
                    {dateTimeLabel(event.startUtc)} ·{" "}
                    {formatCurrency(Number(event.priceAmount), event.currency)} ·{" "}
                    {event.bookedCount}/{event.capacity} booked
                  </p>
                  <p className="admin-item__note">
                    {event.equipmentTypeName ||
                      t("admin.events.inventoryNotConfigured", "Inventory not configured")}{" "}
                    · {event.equipmentQuantityPerAttendee || 1}{" "}
                    {t("admin.events.unitsPerPersonShort", "unit/person")}
                  </p>
                </div>
                <div className="admin-booking__badges">
                  <span
                    className={`admin-pill admin-pill--${
                      event.isActive ? "success" : "neutral"
                    }`}
                  >
                    {event.isActive ? "Active" : "Hidden"}
                  </span>
                </div>
              </div>
              <div className="admin-booking__footer">
                <div className="admin-inline-actions">
                  <button
                    type="button"
                    className="admin-button admin-button--outline"
                    onClick={() => handleEventDuplicate(event)}
                  >
                    <FiCopy aria-hidden="true" />
                    {t("admin.events.scheduleAnother", "Schedule another")}
                  </button>
                  <button
                    type="button"
                    className="admin-button admin-button--ghost"
                    onClick={() =>
                      setExpandedEventId(expandedEventId === event.id ? null : event.id)
                    }
                  >
                    {t("admin.events.people", "People")}
                  </button>
                  <button
                    type="button"
                    className="admin-button admin-button--outline"
                    onClick={() => handleEventEdit(event)}
                  >
                    <FiEdit2 aria-hidden="true" />
                    {t("admin.actions.edit", "Edit")}
                  </button>
                  <button
                    type="button"
                    className="admin-button admin-button--danger"
                    onClick={() => handleEventDelete(event)}
                    disabled={eventActionId === event.id}
                  >
                    <FiTrash2 aria-hidden="true" />
                    {t("admin.actions.delete", "Delete")}
                  </button>
                </div>
              </div>
              {expandedEventId === event.id && (
                <div className="admin-booking__details admin-event__people">
                  <form
                    className="admin-attendee-form"
                    onSubmit={(submitEvent) => handleAddAttendee(submitEvent, event.id)}
                  >
                    <div className="admin-attendee-form__intro">
                      <span className="admin-form__label">
                        {t("admin.events.addAttendee", "Add attendee")}
                      </span>
                    </div>
                    <label className="admin-attendee-form__field">
                      <span className="admin-form__label">
                        {t("admin.events.personName", "Name")}{" "}
                        <span className="admin-field-requirement admin-field-requirement--required">
                          {t("admin.form.required", "Required")}
                        </span>
                      </span>
                      <input
                        className="admin-input"
                        placeholder={t("admin.events.personNamePlaceholder", "Attendee name")}
                        value={attendeeForms[event.id]?.contactName || ""}
                        onChange={(inputEvent) =>
                          handleAttendeeFormChange(event.id, "contactName", inputEvent.target.value)
                        }
                        required
                      />
                    </label>
                    <label className="admin-attendee-form__field">
                      <span className="admin-form__label">
                        {t("admin.events.personEmail", "Email")}{" "}
                        <span className="admin-field-requirement">
                          {t("admin.form.optional", "Optional")}
                        </span>
                      </span>
                      <input
                        className="admin-input"
                        type="email"
                        placeholder="name@example.com"
                        value={attendeeForms[event.id]?.contactEmail || ""}
                        onChange={(inputEvent) =>
                          handleAttendeeFormChange(event.id, "contactEmail", inputEvent.target.value)
                        }
                      />
                    </label>
                    <label className="admin-attendee-form__field">
                      <span className="admin-form__label">
                        {t("admin.events.personPhone", "Phone")}{" "}
                        <span className="admin-field-requirement">
                          {t("admin.form.optional", "Optional")}
                        </span>
                      </span>
                      <input
                        className="admin-input"
                        type="tel"
                        placeholder="+43 ..."
                        value={attendeeForms[event.id]?.contactPhone || ""}
                        onChange={(inputEvent) =>
                          handleAttendeeFormChange(event.id, "contactPhone", inputEvent.target.value)
                        }
                      />
                    </label>
                    <label className="admin-attendee-form__field admin-attendee-form__field--price">
                      <span className="admin-form__label">
                        {t("admin.events.price", "Price")} {" "}
                        <span className="admin-field-requirement">
                          {t("admin.form.optional", "Optional")}
                        </span>
                      </span>
                      <span className="admin-attendee-price-input">
                        <span aria-hidden="true">€</span>
                        <input
                          className="admin-input"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={Number(event.priceAmount || 0).toFixed(2)}
                          value={attendeeForms[event.id]?.priceAmount || ""}
                          onChange={(inputEvent) =>
                            handleAttendeeFormChange(
                              event.id,
                              "priceAmount",
                              inputEvent.target.value
                            )
                          }
                        />
                      </span>
                    </label>
                    <label className="admin-attendee-paid-toggle">
                      <input
                        type="checkbox"
                        checked={Boolean(attendeeForms[event.id]?.isPaid)}
                        onChange={(inputEvent) =>
                          handleAttendeeFormChange(event.id, "isPaid", inputEvent.target.checked)
                        }
                      />
                      <span className="admin-attendee-paid-toggle__control" aria-hidden="true" />
                      <span>
                        <strong>{t("admin.events.markPaid", "Paid")}</strong>
                        <small>{t("admin.events.paymentReceived", "Payment received")}</small>
                      </span>
                    </label>
                    <button
                      type="submit"
                      className="admin-button admin-button--primary"
                      disabled={attendeeActionId === event.id}
                    >
                      <FiUserPlus aria-hidden="true" />
                      {t("admin.events.addPerson", "Add person")}
                    </button>
                  </form>
                  <section className="admin-event-attendees">
                    <header className="admin-event-attendees__header">
                      <div>
                        <span className="admin-form__label">
                          {t("admin.events.attendeeList", "Attendee list")}
                        </span>
                        <p className="admin-item__note">
                          {t(
                            "admin.events.attendeeCount",
                            "{booked} of {capacity} places booked"
                          )
                            .replace("{booked}", String(event.bookedCount ?? 0))
                            .replace("{capacity}", String(event.capacity ?? 0))}
                        </p>
                      </div>
                      <span className="admin-event-attendees__count">
                        {(event.attendees || []).length}
                      </span>
                    </header>
                    {(event.attendees || []).length === 0 ? (
                      <p className="admin-empty admin-event-attendees__empty">
                        {t("admin.events.noAttendees", "No one has booked this event yet.")}
                      </p>
                    ) : (
                      <div className="admin-event-attendees__grid">
                        {event.attendees.map((attendee) => (
                          <article
                            className={`admin-event-attendee-card ${
                              attendee.isManual ? "admin-event-attendee-card--admin-added" : ""
                            }`.trim()}
                            key={attendee.bookingId}
                          >
                            {attendeeEditingId === attendee.bookingId ? (
                              <form
                                className="admin-event-attendee-edit"
                                onSubmit={(submitEvent) =>
                                  handleAttendeeUpdate(
                                    submitEvent,
                                    event.id,
                                    attendee.bookingId
                                  )
                                }
                              >
                                <label className="admin-event-attendee-edit__full">
                                  <span className="admin-form__label">
                                    {t("admin.events.personName", "Name")}
                                  </span>
                                  <input
                                    className="admin-input"
                                    value={attendeeEditForm?.contactName || ""}
                                    onChange={(inputEvent) =>
                                      handleAttendeeEditChange(
                                        "contactName",
                                        inputEvent.target.value
                                      )
                                    }
                                    required
                                  />
                                </label>
                                <label>
                                  <span className="admin-form__label">
                                    {t("admin.events.personEmail", "Email")}
                                  </span>
                                  <input
                                    className="admin-input"
                                    type="email"
                                    value={attendeeEditForm?.contactEmail || ""}
                                    onChange={(inputEvent) =>
                                      handleAttendeeEditChange(
                                        "contactEmail",
                                        inputEvent.target.value
                                      )
                                    }
                                  />
                                </label>
                                <label>
                                  <span className="admin-form__label">
                                    {t("admin.events.personPhone", "Phone")}
                                  </span>
                                  <input
                                    className="admin-input"
                                    type="tel"
                                    value={attendeeEditForm?.contactPhone || ""}
                                    onChange={(inputEvent) =>
                                      handleAttendeeEditChange(
                                        "contactPhone",
                                        inputEvent.target.value
                                      )
                                    }
                                  />
                                </label>
                                <label>
                                  <span className="admin-form__label">
                                    {t("admin.events.price", "Price")}
                                  </span>
                                  <span className="admin-attendee-price-input">
                                    <span aria-hidden="true">€</span>
                                    <input
                                      className="admin-input"
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      placeholder={Number(event.priceAmount || 0).toFixed(2)}
                                      value={attendeeEditForm?.priceAmount ?? ""}
                                      onChange={(inputEvent) =>
                                        handleAttendeeEditChange(
                                          "priceAmount",
                                          inputEvent.target.value
                                        )
                                      }
                                    />
                                  </span>
                                </label>
                                <label className="admin-attendee-paid-toggle admin-attendee-paid-toggle--compact">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(attendeeEditForm?.isPaid)}
                                    onChange={(inputEvent) =>
                                      handleAttendeeEditChange("isPaid", inputEvent.target.checked)
                                    }
                                  />
                                  <span
                                    className="admin-attendee-paid-toggle__control"
                                    aria-hidden="true"
                                  />
                                  <span>
                                    <strong>{t("admin.events.markPaid", "Paid")}</strong>
                                  </span>
                                </label>
                                <div className="admin-event-attendee-edit__actions">
                                  <button
                                    type="submit"
                                    className="admin-button admin-button--primary"
                                    disabled={attendeeActionId === attendee.bookingId}
                                  >
                                    {t("admin.actions.save", "Save")}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-button admin-button--ghost"
                                    onClick={handleAttendeeEditCancel}
                                  >
                                    {t("admin.actions.cancel", "Cancel")}
                                  </button>
                                </div>
                              </form>
                            ) : (
                              <>
                            <header className="admin-event-attendee-card__header">
                              <div className="admin-event-attendee-card__identity">
                                <h4>
                                  {attendee.contactName ||
                                    attendee.contactEmail ||
                                    t("admin.events.anonymousAttendee", "Manual attendee")}
                                </h4>
                                <p>{attendee.contactEmail || t("admin.bookings.detail.none", "—")}</p>
                              </div>
                              <span
                                className={`admin-pill admin-pill--${
                                  attendee.paymentStatus === "Succeeded" ? "success" : "neutral"
                                }`}
                              >
                                {attendee.paymentStatus === "Succeeded"
                                  ? t("admin.events.paidAttendee", "Paid")
                                  : t("admin.events.unpaidAttendee", "Unpaid")}
                              </span>
                            </header>
                            <div className="admin-event-attendee-card__details">
                              <div>
                                <span className="admin-detail-label">
                                  {t("admin.events.personPhone", "Phone")}
                                </span>
                                <p className="admin-detail-value">
                                  {attendee.contactPhone || t("admin.bookings.detail.none", "—")}
                                </p>
                              </div>
                              <div>
                                <span className="admin-detail-label">
                                  {t("admin.events.amount", "Amount")}
                                </span>
                                <p className="admin-detail-value">
                                  {formatCurrency(
                                    Number(attendee.priceAmount),
                                    attendee.currency
                                  )}
                                </p>
                              </div>
                            </div>
                            <p className="admin-event-attendee-card__meta">
                              <span>{attendee.status}</span>
                              <span>{attendee.paymentStatus}</span>
                              <span>{formatTimestamp(attendee.createdUtc)}</span>
                            </p>
                            <footer className="admin-event-attendee-card__footer">
                              <div className="admin-event-attendee-card__actions">
                                {attendee.isManual && (
                                  <button
                                    type="button"
                                    className="admin-button admin-button--ghost"
                                    onClick={() => handleAttendeeEdit(event, attendee)}
                                    disabled={attendeeActionId === attendee.bookingId}
                                  >
                                    <FiEdit2 aria-hidden="true" />
                                    {t("admin.actions.edit", "Edit")}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="admin-button admin-button--danger-outline"
                                  onClick={() => handleRemoveAttendee(event.id, attendee)}
                                  disabled={attendeeActionId === attendee.bookingId}
                                >
                                  <FiTrash2 aria-hidden="true" />
                                  {t("admin.events.removePerson", "Remove")}
                                </button>
                              </div>
                              {!attendee.isManual && (
                                <p className="admin-item__note">
                                  {t(
                                    "admin.events.paidBookingNote",
                                    "Website booking. Removing it cancels the booking; refunds are managed separately in Bookings."
                                  )}
                                </p>
                              )}
                            </footer>
                              </>
                            )}
                          </article>
                        ))}
                      </div>
                    )}
                  </section>
                </div>
              )}
            </article>
            </React.Fragment>
          ))
        )}
      </div>
    </section>
  </div>
  );
};

export default AdminEventsTab;
